import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function MacroSplitCalculator() {
  const [calories, setCalories] = useState("");
  const [goal, setGoal] = useState("balanced");
  const [results, setResults] = useState<any>(null);

  const calculateMacros = () => {
    const cal = parseFloat(calories);
    if (!cal || cal <= 0) return;
    
    let proteinPercent, carbsPercent, fatPercent;
    
    switch (goal) {
      case "weight-loss":
        proteinPercent = 40; carbsPercent = 30; fatPercent = 30;
        break;
      case "muscle-gain":
        proteinPercent = 35; carbsPercent = 45; fatPercent = 20;
        break;
      case "low-carb":
        proteinPercent = 40; carbsPercent = 20; fatPercent = 40;
        break;
      default: // balanced
        proteinPercent = 30; carbsPercent = 40; fatPercent = 30;
    }
    
    const proteinCal = cal * (proteinPercent / 100);
    const carbsCal = cal * (carbsPercent / 100);
    const fatCal = cal * (fatPercent / 100);
    
    setResults({
      protein: { grams: Math.round(proteinCal / 4), percent: proteinPercent, calories: Math.round(proteinCal) },
      carbs: { grams: Math.round(carbsCal / 4), percent: carbsPercent, calories: Math.round(carbsCal) },
      fat: { grams: Math.round(fatCal / 9), percent: fatPercent, calories: Math.round(fatCal) }
    });
  };

  return (
    <PageLayout calculatorName="Macro Split Calculator" calculatorPath="/calculator/macro-split">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Macros</CardTitle>
            <CardDescription>Enter your daily calorie target</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Daily Calories</Label>
              <Input type="number" value={calories} onChange={(e) => setCalories(e.target.value)} placeholder="2000" />
            </div>
            <div>
              <Label>Goal</Label>
              <Select value={goal} onValueChange={setGoal}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="balanced">Balanced (30/40/30)</SelectItem>
                  <SelectItem value="weight-loss">Weight Loss (40/30/30)</SelectItem>
                  <SelectItem value="muscle-gain">Muscle Gain (35/45/20)</SelectItem>
                  <SelectItem value="low-carb">Low Carb (40/20/40)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={calculateMacros} className="w-full">Calculate Macros</Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Your Macro Split</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="border-b pb-3">
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold">Protein</span>
                      <span className="text-primary">{results.protein.percent}%</span>
                    </div>
                    <div className="text-2xl font-bold">{results.protein.grams}g</div>
                    <div className="text-sm text-muted-foreground">{results.protein.calories} calories</div>
                  </div>
                  <div className="border-b pb-3">
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold">Carbohydrates</span>
                      <span className="text-primary">{results.carbs.percent}%</span>
                    </div>
                    <div className="text-2xl font-bold">{results.carbs.grams}g</div>
                    <div className="text-sm text-muted-foreground">{results.carbs.calories} calories</div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold">Fat</span>
                      <span className="text-primary">{results.fat.percent}%</span>
                    </div>
                    <div className="text-2xl font-bold">{results.fat.grams}g</div>
                    <div className="text-sm text-muted-foreground">{results.fat.calories} calories</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="md:col-span-2">
              <CalculatorSuperCalcCTA config={calculatorCTAs["macro-split"]} trigger={results.protein.grams} />
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
